import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Markdown from '../../../../components/Markdown';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { IconButton } from '@chakra-ui/react';
import { GetAiAssistantsQuery } from '../../../../generated/graphqlModels';
import Header from './Header';

import './styles.css';
import useThread from './useThread';
import { useQuery } from '@apollo/client';
import { GET_THREADS_BY_ASSISTANT_WITH_MESSAGES } from '../../queries';
import { AuthContext } from '../../../../context/AuthProvider';
import { formatAiResponseMarkdown } from '../../../../helpers/textFormatting';
import { StemiBeatLoader } from '../../../../components/Loader';
interface ContainerProps {
  assistants: GetAiAssistantsQuery['aiAssistants'];
  isTeamChatVisible?: boolean;
  isBlock?: boolean;
  blockId?: string;
  preferedThreadId?: string;
  onClose?: () => void;
}

const clearUnwantedMessageParts = (messageContent: string) => {
  messageContent = messageContent.replace(/【.*†source】/, '');
  return messageContent;
};

const Container = (props: ContainerProps) => {
  const { t } = useTranslation();
  const user = useContext(AuthContext);
  const {
    assistants,
    // isTeamChatVisible,
    isBlock,
    blockId,
    // preferedThreadId
  } = props;
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    assistants[0]?.id || null,
  );
  const selectedAssistant =
    assistants.find((a) => a.id === selectedAssistantId) || assistants[0]!;

  const [preferedThreadId, setPreferedThreadId] = useState<string>('');
  const {
    thread,
    isLoading: isThreadLoading,
    isCreatingMessage,
    sendMessage,
    clearThread,
    switchThread,
  } = useThread(selectedAssistant?.id, blockId, preferedThreadId);

  const [messageToSend, setMessageToSend] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  const [viewState, setViewState] = useState<'closed' | 'normal' | 'expanded'>(
    'normal',
  );

  const chatHistoryRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [thread]);

  useEffect(() => {
    if (isCreatingMessage) {
      scrollToBottom();
    } else {
      setPendingMessage('');
    }
  }, [isCreatingMessage]);

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  const addMessage = () => {
    if (!messageToSend.trim()) return;
    setPendingMessage(messageToSend);
    sendMessage(messageToSend);
    setMessageToSend('');
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      addMessage();
    }
  };

  const toggleExpand = () => {
    if (viewState === 'normal') {
      setViewState('expanded');
    } else if (viewState === 'expanded') {
      setViewState('normal');
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    // don't close chat  on click outside for block mode
    if (isBlock) return;

    const confirmationModal = document.querySelector('.confirmation-modal');

    if (
      confirmationModal &&
      (confirmationModal.contains(event.target as Node) ||
        confirmationModal.contains(containerRef.current))
    ) {
      // don't close chat if clicking inside modal or if modal is open
      return;
    }

    // Only check if the click was outside THIS specific container
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setViewState('closed');
      props.onClose?.();
    }
  };

  useEffect(() => {
    if (!isBlock) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isBlock]);

  const sortedMessages = thread?.messages
    ? [...thread.messages].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
    : [];

  const { data: threadsData } = useQuery(
    GET_THREADS_BY_ASSISTANT_WITH_MESSAGES,
    {
      variables: { assistantId: selectedAssistant.id, userId: user!.pk },
      skip: !selectedAssistant.id || !user,
    },
  );

  const handleThreadSelect = (threadId: string) => {
    setMessageToSend('');
    setPendingMessage('');
    switchThread(threadId);
    setPreferedThreadId(threadId);
  };

  return (
    <div
      ref={containerRef}
      className={cn('chat-container', {
        'chat-container-expanded': viewState === 'expanded',
        'chat-container-block': isBlock,
      })}
    >
      <div className="chat">
        <Header
          selectedAssistant={selectedAssistant}
          assistants={assistants}
          setSelectedAssistantId={setSelectedAssistantId}
          clearThread={clearThread}
          isExpanded={viewState === 'expanded'}
          onToggleExpand={toggleExpand}
          threads={threadsData?.threads.filter(
            (thread: any) => thread.messages.length > 2,
          )} // filtering out threads with less than 2 messages because first two are just Martin greetings
          onThreadSelect={handleThreadSelect}
          isBlock={isBlock}
        />

        <div className="chat-history" ref={chatHistoryRef}>
          {isThreadLoading ? (
            <div className="chat-history-loading-container">
              <StemiBeatLoader />
            </div>
          ) : (
            <ul>
              {sortedMessages.map((message, index) => {
                if (message.role === 'system') {
                  return null;
                }
                const hasContent = message.content || message.suggestion;
                if (!hasContent) {
                  return null;
                }
                const isUserMessage = message.role === 'user';
                if (
                  !isUserMessage &&
                  selectedAssistant.type === 'image' &&
                  message.content
                ) {
                  return (
                    <li
                      className={`clearfix ${
                        isUserMessage ? 'float-right-container' : ''
                      }`}
                    >
                      <div
                        style={{ padding: '0px', background: 'none' }}
                        className={`chat-message ${
                          isUserMessage
                            ? 'chat-other-message float-right'
                            : 'chat-my-message'
                        }`}
                      >
                        <img src={message.content} alt="AI generated" />
                      </div>
                    </li>
                  );
                }
                return (
                  <React.Fragment key={index}>
                    {message.content && (
                      <li
                        className={`clearfix ${
                          isUserMessage ? 'float-right-container' : ''
                        }`}
                      >
                        <div
                          className={`chat-message ${
                            isUserMessage
                              ? 'chat-other-message float-right'
                              : 'chat-my-message'
                          }`}
                        >
                          <Markdown
                            markdown={formatAiResponseMarkdown(
                              clearUnwantedMessageParts(message.content),
                            )}
                          />
                        </div>
                      </li>
                    )}
                    {message.suggestion && (
                      <li className="clearfix">
                        <div className={'chat-message chat-my-message'}>
                          <Markdown markdown={message.suggestion} />
                        </div>
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
              {pendingMessage && (
                <li className="clearfix float-right-container">
                  <div className="chat-message chat-other-message float-right">
                    {pendingMessage}
                  </div>
                </li>
              )}
              {isCreatingMessage && (
                <li className="clearfix">
                  <div className="chat-message chat-my-message typing-indicator">
                    <span>
                      {t('aiContainer.isTyping', 'AI assistant is typing')}
                    </span>
                    <span className="dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="chat-message">
          <textarea
            disabled={isCreatingMessage}
            name="message-to-send"
            id="message-to-send"
            placeholder={t('aiContainer.typeYourMessage')}
            rows={3}
            value={messageToSend}
            onChange={(e) => setMessageToSend(e.target.value)}
            onKeyDown={handleKeyDown}
          ></textarea>

          <IconButton
            onClick={addMessage}
            aria-label={t('aiContainer.send')}
            icon={<ArrowForwardIcon />}
            colorScheme="blue"
            size="md"
            ml={2}
            disabled={isCreatingMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default Container;
